import React, { useMemo, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { FaExclamationTriangle, FaTrash } from 'react-icons/fa';

export default function DeleteWithReasonModal({ isOpen, onClose, title, subjectLabel, onConfirm }) {
  const [reason, setReason] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');

  const canDelete = useMemo(() => String(reason || '').trim().length >= 10, [reason]);

  const handleClose = () => {
    setReason('');
    setError('');
    onClose?.();
  };

  const handleDelete = async () => {
    if (!canDelete) {
      setError('Por favor, forneça um motivo detalhado (mínimo 10 caracteres)');
      return;
    }
    setIsDeleting(true);
    setError('');
    try {
      await onConfirm?.(String(reason).trim());
      setReason('');
      onClose?.();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Erro ao excluir:', err);
      setError(err?.message || 'Erro ao excluir. Tente novamente.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} closeOnOverlayClick={false} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <Icon as={FaTrash} color="red.500" />
            <Text>{title || 'Confirmar exclusão'}</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Alert status="warning">
              <AlertIcon />
              <Box>
                <Text fontWeight="bold">Atenção!</Text>
                <Text fontSize="sm">Esta ação não pode ser desfeita. Um registro será mantido para auditoria.</Text>
              </Box>
            </Alert>

            {subjectLabel ? (
              <Box p={3} bg="gray.50" borderRadius="md">
                <Text fontWeight="semibold" mb={1}>Item a ser excluído:</Text>
                <Text color="red.600" fontWeight="bold" noOfLines={2}>{subjectLabel}</Text>
              </Box>
            ) : null}

            <FormControl isInvalid={!!error} isRequired>
              <FormLabel>
                <HStack>
                  <Icon as={FaExclamationTriangle} color="orange.500" boxSize={3} />
                  <Text>Motivo da exclusão</Text>
                </HStack>
              </FormLabel>
              <Textarea
                placeholder="Descreva o motivo (mínimo 10 caracteres)..."
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  setError('');
                }}
                minHeight="100px"
                isDisabled={isDeleting}
              />
              <FormErrorMessage>{error}</FormErrorMessage>
              <Text fontSize="xs" color="gray.500" mt={1}>
                {String(reason || '').length}/10 caracteres mínimos
              </Text>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose} isDisabled={isDeleting}>
            Cancelar
          </Button>
          <Button
            colorScheme="red"
            onClick={handleDelete}
            isLoading={isDeleting}
            loadingText="Excluindo..."
            isDisabled={!canDelete}
            leftIcon={<Icon as={FaTrash} />}
          >
            Confirmar exclusão
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

